import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { MapPin, Zap, Building2, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { projectsAPI } from '../services/api';
import { projects as mockProjects } from '../data/mock';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const loadProject = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await projectsAPI.getAll();
      const data = response.data.map(p => ({ ...p, id: p._id, type: p.project_type }));
      const found = data.find(p => p.slug === slug);
      if (found) {
        setProject(found);
      } else {
        const mockFound = mockProjects.find(p => p.slug === slug);
        if (mockFound) {
          setProject(mockFound);
        } else {
          setNotFound(true);
        }
      }
    } catch {
      const mockFound = mockProjects.find(p => p.slug === slug);
      if (mockFound) {
        setProject(mockFound);
      } else {
        setNotFound(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadProject();
  }, [slug, loadProject]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/projects" replace />;
  }

  if (!project) return null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
  };

  return (
    <div className="min-h-screen pt-20">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/projects"
            className="inline-flex items-center text-gray-600 hover:text-emerald-600 transition-colors duration-200"
            data-testid="back-to-projects"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Projects
          </Link>
        </div>
      </div>

      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-4 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium">
              {project.type || project.project_type}
            </span>
            <span className="px-4 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium flex items-center gap-1">
              <Zap className="w-4 h-4" />
              {project.mw} MW
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="project-title">{project.title}</h1>
          <div className="flex items-center gap-2 text-emerald-50">
            <MapPin className="w-5 h-5" />
            <span className="text-lg">{project.location}</span>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="aspect-video rounded-2xl overflow-hidden bg-gray-100">
              <img
                src={project.images[currentImageIndex]}
                alt={`${project.title} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
            {project.images.length > 1 && (
              <>
                <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-gray-800 hover:bg-white transition-all duration-200" aria-label="Previous image">
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-gray-800 hover:bg-white transition-all duration-200" aria-label="Next image">
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="flex justify-center gap-2 mt-6">
                  {project.images.map((img, index) => (
                    <button
                      key={img}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${index === currentImageIndex ? 'w-8 bg-emerald-600' : 'w-2 bg-gray-300'}`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Summary</h2>
                <p className="text-gray-700 leading-relaxed text-lg">{project.summary}</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Challenges</h2>
                <p className="text-gray-700 leading-relaxed">{project.challenges}</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Approach</h2>
                <p className="text-gray-700 leading-relaxed">{project.approach}</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Outcome</h2>
                <p className="text-gray-700 leading-relaxed text-lg font-medium">{project.outcome}</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Project Details</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Capacity</p>
                    <p className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-emerald-600" />
                      {project.mw} MW
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Location</p>
                    <p className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                      {project.location}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Project Type</p>
                    <p className="text-lg font-semibold text-gray-900">{project.type || project.project_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Client</p>
                    <p className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-emerald-600" />
                      {project.client}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-8 shadow-lg text-white">
                <h3 className="text-xl font-bold mb-4">Interested in Similar Solutions?</h3>
                <p className="mb-6 text-emerald-50">Contact us to discuss how we can help with your renewable energy project.</p>
                <Link to="/contact" className="inline-flex items-center justify-center w-full px-6 py-3 bg-white text-emerald-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  Get In Touch
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
